/**
 * Inscription et connexion (backlog **M2.14**) — SPEC-API §4.4.
 *
 * Un seul écran, deux onglets : quatre-vingt-dix pour cent des joueurs n'ont pas de compte à
 * leur première visite et en auront un à la seconde, et devoir chercher « l'autre lien » est le
 * meilleur moyen de perdre les deux.
 *
 * **Ce que ce fichier ne fait pas, et ne fera jamais** : stocker un mot de passe, poser un
 * cookie, ou décider qu'une session est valide. Tout cela appartient à better-auth côté serveur
 * (§4.1) ; ici on remplit un formulaire, on l'envoie, et on montre la phrase qui revient.
 *
 * Les bornes affichées (8 à 128 caractères) sont celles du **serveur** : les montrer avant
 * l'échec, c'est éviter au joueur d'apprendre la règle en la ratant.
 *
 * ⟦C57 · lot 6 · A-174⟧ **Moins de texte.** L'accroche, l'aide du pseudo, celle du mot de passe et la note
 * du bas tenaient 82 à 114 mots. Chacune garde UNE phrase courte à l'écran (ou un mot dans le champ) ; leur
 * version d'origine, mot pour mot, vit dans le bouton « 📰 Les dessous » à côté de l'accroche
 * (`components/LesDessous.tsx`). Les bornes (8 à 128, l'aide « 8 caractères minimum ») restent à l'écran.
 */

import { useState, type FormEvent } from "react";

import { MDP_MAX, MDP_MIN } from "../../api/auth";
import { EcranFenetre } from "../../components/EcranFenetre";
import { DessousBloc, LesDessous } from "../../components/LesDessous";
import { LogoMeTube } from "../../components/LogoMeTube";
import { useSessionStore } from "../../stores/sessionStore";

export function Authentification() {
  const vue = useSessionStore((s) => s.vue);
  const setVue = useSessionStore((s) => s.setVue);
  const erreurServeur = useSessionStore((s) => s.erreur);
  const enCours = useSessionStore((s) => s.enCours);
  const inscrire = useSessionStore((s) => s.inscrire);
  const connecter = useSessionStore((s) => s.connecter);
  const choisir = useSessionStore((s) => s.choisir);
  const revenirAuMenu = useSessionStore((s) => s.revenirAuMenu);

  const [pseudo, setPseudo] = useState("");
  const [email, setEmail] = useState("");
  const [motDePasse, setMotDePasse] = useState("");
  const [erreurLocale, setErreurLocale] = useState<string | null>(null);

  const inscription = vue === "inscription";
  const erreur = erreurLocale ?? erreurServeur;

  async function soumettre(e: FormEvent) {
    e.preventDefault();
    setErreurLocale(null);

    const adresse = email.trim();
    // Validation minimale et **honnête** : on ne réimplémente pas la RFC 5322 côté client, le
    // serveur reste juge (§4.4 rend `INVALID_EMAIL`). On attrape seulement l'évidence.
    if (!adresse.includes("@") || adresse.length < 5) {
      setErreurLocale("Il manque une adresse e-mail valide.");
      return;
    }
    if (motDePasse.length < MDP_MIN || motDePasse.length > MDP_MAX) {
      setErreurLocale(`Le mot de passe fait entre ${MDP_MIN} et ${MDP_MAX} caractères.`);
      return;
    }
    if (inscription) {
      const nom = pseudo.trim();
      if (nom.length < 2) {
        setErreurLocale("Un pseudo, même court : c'est lui qu'on affichera.");
        return;
      }
      await inscrire({ name: nom, email: adresse, password: motDePasse });
      return;
    }
    await connecter({ email: adresse, password: motDePasse });
  }

  return (
    /* ⟦C48 · A-168 §3⟧ Une `Fenetre` sur le fond du menu (`EcranFenetre.tsx`) : le titre et
       l'étiquette « compte » sont dans le bandeau, avec le vrai logo pixel de MeTube pour icône.
       Avant, c'était une carte de papier sur l'aplat jaune — la seule entrée du jeu hors DA. */
    <EcranFenetre
      titre={inscription ? "Ouvrir un compte" : "Se connecter"}
      icone={<LogoMeTube className="logo-metube logo-metube-ruban" />}
      badge="compte : partie sur le serveur"
      className="fenetre-auth"
    >
      {/* ⟦C57 · lot 6⟧ Une phrase à l'écran, et le bouton qui rouvre le reste, juste à côté : l'accroche
          d'origine, l'aide du pseudo, celle du mot de passe, et la fin de la note du bas (tout le jeu,
          dans ton navigateur, sans rien enregistrer ailleurs), mot pour mot. */}
      <div className="auth-accroche">
        <p className="accroche">
          {inscription
            ? "Ta chaîne est sauvegardée à chaque action."
            : "Ta chaîne t'attend, au tour exact."}
        </p>
        <LesDessous titre={inscription ? "Ouvrir un compte" : "Se connecter"}>
          <DessousBloc titre="Ta chaîne">
            <p className="dessous-texte">
              {inscription
                ? "Une adresse, un mot de passe, et ta chaîne est sauvegardée à chaque action — pas à chaque fermeture d'onglet."
                : "Ta chaîne t'attend là où tu l'as laissée : le serveur a gardé le tour exact."}
            </p>
          </DessousBloc>
          {inscription ? (
            <DessousBloc titre="Pseudo">
              <p className="dessous-texte">
                Ce n'est pas le nom de ta chaîne : celui-là se choisit à la création de la run. Le pseudo,
                c'est le nom sous lequel on te connaît.
              </p>
            </DessousBloc>
          ) : null}
          <DessousBloc titre="Mot de passe">
            <p className="dessous-texte">
              {MDP_MIN} caractères minimum. Aucune vérification par e-mail : il n'y a pas de service
              d'envoi, donc rien à attendre dans ta boîte.
            </p>
          </DessousBloc>
          <DessousBloc titre="Sans compte">
            <p className="dessous-texte">
              Jouer en bac à sable : tout le jeu, dans ton navigateur, sans rien enregistrer ailleurs.
            </p>
          </DessousBloc>
        </LesDessous>
      </div>

      <div className="onglets-auth" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={!inscription}
          className={`bouton ${inscription ? "discret" : "primaire"}`}
          onClick={() => {
            setVue("connexion");
            setErreurLocale(null);
          }}
        >
          J'ai déjà un compte
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={inscription}
          className={`bouton ${inscription ? "primaire" : "discret"}`}
          onClick={() => {
            setVue("inscription");
            setErreurLocale(null);
          }}
        >
          J'en crée un
        </button>
      </div>

      <form className="formulaire" onSubmit={(e) => void soumettre(e)}>
        {inscription ? (
          <label className="champ">
            <span className="champ-label">Pseudo</span>
            <input
              className="champ-saisie"
              value={pseudo}
              onChange={(e) => {
                setPseudo(e.target.value);
                setErreurLocale(null);
              }}
              placeholder="Pas le nom de ta chaîne"
              autoComplete="nickname"
              maxLength={40}
              autoFocus
            />
          </label>
        ) : null}

        <label className="champ">
          <span className="champ-label">Adresse e-mail</span>
          <input
            className="champ-saisie"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErreurLocale(null);
            }}
            placeholder="toi@exemple.fr"
            autoComplete="email"
            autoFocus={!inscription}
          />
        </label>

        <label className="champ">
          <span className="champ-label">Mot de passe</span>
          <input
            className="champ-saisie"
            type="password"
            value={motDePasse}
            onChange={(e) => {
              setMotDePasse(e.target.value);
              setErreurLocale(null);
            }}
            autoComplete={inscription ? "new-password" : "current-password"}
            maxLength={MDP_MAX}
          />
          <span className="champ-aide">{MDP_MIN} caractères minimum</span>
        </label>

        {erreur ? <p className="erreur">{erreur}</p> : null}

        <button className="bouton primaire grand" type="submit" disabled={enCours}>
          {enCours ? "…" : inscription ? "🔐 Créer le compte" : "🔐 Entrer"}
        </button>
      </form>

      <p className="note-bas">
        Pas envie de compte ?{" "}
        <button type="button" className="lien" onClick={() => void choisir("bac-a-sable")}>
          Jouer en bac à sable
        </button>{" "}
        ou{" "}
        <button type="button" className="lien" onClick={revenirAuMenu}>
          revenir au menu
        </button>
        .
      </p>
    </EcranFenetre>
  );
}
